import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEvent, createStore, sample } from "effector";
import { IStreakContract } from "../model";
import { IStreakPeriod, StreakType, WidgetType } from "@features/shared/entities/streak";
import { reset } from "patronum";
import { getRandomItemFromArray, streakWidgetTitles } from "@features/helpers";
import { startOfDay, subDays } from "date-fns";
import { STREAK_MODAL_HEADERS, STREAK_MODAL_SUBHEADERS } from "@data/mocks";
import { IFlagsmithContract } from "@features/flagsmith/model";

const lastStreakStatusChanged = createEvent<StreakType>();

const $lastStreakStatus = createStore<StreakType>(StreakType.DEFAULT).on(lastStreakStatusChanged, (_, status) => status);
const $lastStreak = createStore<IStreakPeriod | null>(null);
const $titleForWidget = createStore<string>("");
const $widgetType = createStore<WidgetType>("small");

const $streakModalTextData = createStore<{ header: string; subheader: string; days: number }>({
    header: "",
    subheader: "",
    days: 0
});
const contract = new ModelContract(
    { $lastStreak, $lastStreakStatus, $titleForWidget, $streakModalTextData, $widgetType },
    { lastStreakStatusChanged },
    {},
    {}
);

export type IStreakWidgetContract = typeof contract;

export class StreakWidgetModule extends ModuleDescription<IStreakWidgetContract> {
    constructor(
        public streakModule: IStreakContract,
        public flagsmithModule: IFlagsmithContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: this.streakModule.effects.getStreakListFx.doneData,
            filter: (data) => data !== null,
            fn: (data) => data![0],
            target: $lastStreak
        });

        sample({
            clock: this.streakModule.effects.getStreakListFx.doneData,
            fn: (data) => {
                if (!data) return StreakType.TODAY;

                const endDate = new Date(data[0].EndDate);
                const today = new Date();

                if (endDate < startOfDay(subDays(today, 1))) {
                    return StreakType.BROKEN;
                }

                return StreakType.DONE;
            },
            target: $lastStreakStatus
        });

        sample({
            source: $lastStreakStatus,
            fn: (status) => {
                const set = streakWidgetTitles.find((t) => t.type === status);

                if (!set) return "";

                // Returns the natural-key template; {{days}} is interpolated at render.
                return set.titles[Math.floor(Math.random() * set.titles.length)];
            },
            target: $titleForWidget
        });

        sample({
            clock: this.streakModule.stores.$streakStats,
            source: $streakModalTextData,
            filter: (text, data) => data !== null && (text.header === "" || text.days !== data.currentStreak),
            fn: (_, data) => {
                // Store raw natural-key templates; {{days}} is interpolated at render.
                const header = getRandomItemFromArray(STREAK_MODAL_HEADERS);
                const subheader = getRandomItemFromArray(STREAK_MODAL_SUBHEADERS);
                return { header, subheader, days: data!.currentStreak };
            },
            target: $streakModalTextData
        });


        reset({
            clock: this.streakModule.events.storesReset,
            target: [$lastStreakStatus, $titleForWidget]
        });
    }
}
